"use client";

import { type FormEvent, useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useAuth } from "@/lib/auth";
import { getAuthReturnTo, saveAuthReturnToFromQuery } from "@/lib/auth-return";
import AuthPageHeader from "@/components/auth/AuthPageHeader";
import GoogleAuthButton from "@/components/auth/GoogleAuthButton";
import AuthDivider from "@/components/auth/AuthDivider";

export default function LoginPage() {
  const { signInWithGoogle, signInWithEmail } = useAuth();
  const router = useRouter();
  const [error, setError] = useState<string | null>(null);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // 大学からの招待メールなど、外部の導線が `next` で復帰先を指定してくることがある。
  // 既存の復帰先（sessionStorage）へ寄せておき、認証後の遷移は共通の仕組みに任せる。
  useEffect(() => {
    saveAuthReturnToFromQuery();
  }, []);

  const isLoading = isGoogleLoading || isEmailLoading;

  const handleGoogleLogin = async () => {
    setError(null);
    setIsGoogleLoading(true);
    const returnTo = getAuthReturnTo();
    const { error } = await signInWithGoogle(
      returnTo ? { redirectTo: returnTo } : undefined
    );
    if (error) {
      setError(error);
      setIsGoogleLoading(false);
    }
  };

  const handleEmailLogin = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsEmailLoading(true);
    const { error } = await signInWithEmail(email, password);
    if (error) {
      setError(error);
      setIsEmailLoading(false);
      return;
    }
    // authReturnTo がある場合は onAuthStateChange 内で claim → redirect が実行される
    const returnTo = getAuthReturnTo();
    if (!returnTo) {
      router.push("/dashboard");
    }
  };

  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <AuthPageHeader
          title="ログイン"
          subtitle="アカウントにログインして、面接練習を続けましょう"
        />

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        <GoogleAuthButton
          label="Googleでログイン"
          loadingLabel="リダイレクト中..."
          isLoading={isLoading}
          onClick={handleGoogleLogin}
        />

        <AuthDivider />

        <form onSubmit={handleEmailLogin} className="space-y-4">
          <div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="メールアドレス"
              required
              disabled={isLoading}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
          <div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="パスワード"
              required
              minLength={6}
              disabled={isLoading}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <div className="text-right mt-1">
              <Link
                href="/reset-password"
                className="text-sm text-accent hover:text-accent-hover"
              >
                パスワードをお忘れですか？
              </Link>
            </div>
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-accent hover:bg-accent-hover text-on-accent px-4 py-3 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isEmailLoading ? "ログイン中..." : "ログイン"}
          </button>
        </form>

        <p className="text-center text-sm text-gray-500 mt-6">
          アカウントをお持ちでないですか？{" "}
          <Link
            href="/signup"
            className="text-accent hover:text-accent-hover font-medium"
          >
            新規登録
          </Link>
        </p>
      </div>
    </div>
  );
}
